import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiPatch } from '../services/api';
import ChangePasswordModal from '../components/ChangePasswordModal';

const ROLE_LABELS = {
  administrador: 'Administrador',
  coordenador: 'Coordenador',
  supervisor: 'Supervisor',
  diretor: 'Diretor(a) Escolar',
  gestor: 'Gestor (Secretário Executivo da SME)',
  referencia_tecnica: 'Referência Técnica do Município',
  professor: 'Professor',
};

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function Perfil() {
  const { user, updateUser } = useAuth();
  const { darkMode } = useTheme();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const initials = (() => {
    const base = `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
      || user?.username
      || user?.email
      || '?';
    return base
      .split(/\s+/)
      .slice(0, 2)
      .map(w => w.charAt(0).toUpperCase())
      .join('');
  })();

  const startEdit = () => {
    setForm({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    });
    setError(null);
    setSuccess(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError(null);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.first_name.trim()) {
      setError('O nome é obrigatório.');
      return;
    }
    if (!form.email.trim()) {
      setError('O email é obrigatório.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = await apiPatch('/users/me/', {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone?.trim() || null,
      });
      updateUser(updated);
      setEditing(false);
      setSuccess('Dados atualizados com sucesso.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const data = err?.response?.data || {};
      setError(
        data.email?.[0]
        || data.detail
        || err.message
        || 'Não foi possível atualizar os dados.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className={`min-h-screen p-4 sm:p-6 ${darkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
      <header className="mb-6">
        <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
          Meu Perfil
        </h1>
        <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          Veja e atualize seus dados de contato e a senha de acesso.
        </p>
      </header>

      {success && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${darkMode
          ? 'bg-[#13983D]/15 border border-[#13983D]/30 text-[#13983D]'
          : 'bg-[#13983D]/10 border border-[#13983D]/20 text-[#13983D]'
        }`}>
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cartão do usuário */}
        <div className={`rounded-2xl p-6 ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/40'
          : 'bg-white border border-slate-200 shadow-sm'
        }`}>
          <div className="flex items-center gap-4 mb-6">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #0974BA, #F9B047)' }}
            >
              {initials}
            </div>
            <div className="min-w-0">
              <h2 className={`text-lg font-bold truncate ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username}
              </h2>
              <p className={`text-xs truncate ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                @{user.username}
              </p>
              <span className={`inline-block mt-2 px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase ${darkMode
                ? 'bg-[#0974BA]/20 text-[#0974BA]'
                : 'bg-[#0974BA]/10 text-[#0974BA]'
              }`}>
                {ROLE_LABELS[user.role] || user.role}
              </span>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <div>
              <p className={`text-xs uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Cidade</p>
              <p className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{user.city_name || '—'}</p>
            </div>
            {user.teacher_name && (
              <div>
                <p className={`text-xs uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Cadastro de Professor</p>
                <p className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{user.teacher_name}</p>
              </div>
            )}
            <div>
              <p className={`text-xs uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Último acesso</p>
              <p className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{formatDate(user.last_login)}</p>
            </div>
            <div>
              <p className={`text-xs uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Conta criada em</p>
              <p className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{formatDate(user.created_at || user.date_joined)}</p>
            </div>
          </div>

          <button
            onClick={() => setShowPasswordModal(true)}
            className="w-full mt-6 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg"
          >
            Trocar senha
          </button>
        </div>

        {/* Dados editáveis */}
        <div className={`lg:col-span-2 rounded-2xl p-6 ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/40'
          : 'bg-white border border-slate-200 shadow-sm'
        }`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-base font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Dados de contato
            </h3>
            {!editing && (
              <button
                onClick={startEdit}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${darkMode
                  ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Editar
              </button>
            )}
          </div>

          {editing ? (
            <form className="space-y-4" onSubmit={handleSave}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Sobrenome
                  </label>
                  <input
                    type="text"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                  />
                </div>
              </div>

              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Email *
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                  } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                />
              </div>

              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Telefone
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(00) 00000-0000"
                  className={`w-full px-3 py-2 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500'
                    : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                  } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                />
              </div>

              {error && (
                <div className={`p-3 rounded-lg text-sm ${darkMode
                  ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20 text-[#E83A3C]'
                  : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20 text-[#E83A3C]'
                }`}>
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={saving}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${darkMode
                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg disabled:opacity-60"
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className={`text-xs uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nome</p>
                <p className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{user.first_name || '—'}</p>
              </div>
              <div>
                <p className={`text-xs uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Sobrenome</p>
                <p className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{user.last_name || '—'}</p>
              </div>
              <div>
                <p className={`text-xs uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Email</p>
                <p className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{user.email || '—'}</p>
              </div>
              <div>
                <p className={`text-xs uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Telefone</p>
                <p className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{user.phone || '—'}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSuccess={() => {
          setShowPasswordModal(false);
          setSuccess('Senha alterada com sucesso.');
          setTimeout(() => setSuccess(null), 3000);
        }}
      />
    </div>
  );
}

export default Perfil;
